// @flow
import { createUpdateResourceSettings } from 'api'
import PrivateRoute from 'components/PrivateRoute'
import LayoutWithSidebar from 'pages/Components/LayoutWithSidebar'
import FormBuilderSettingsCustomInvite from 'pages/FormBuilderSettingsCustomInvite'
import FormBuilderSettingsNotifications from 'pages/FormBuilderSettingsNotifications'
import PacketDetails from 'pages/PacketDetails'
import PacketSideMenu from 'pages/PacketDetails/Components/PacketSideMenu'
import { setErrors } from 'pages/Slices/packetSlice'
import { saveExtraContexts } from 'pages/Utils/extra-contexts'
import { useAddPackets } from 'queries/packets/useAddPackets'
import { useUpdatePackets } from 'queries/packets/useUpdatePackets'
import * as React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Switch, useHistory, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import { useAddNewCompletePageConditionalRules } from '@/queries/questions/useAddNewCompletePageConditionalRules'
import { useDeleteCompletePageConditionalRuleById } from '@/queries/questions/useDeleteCompletePageConditionalRuleById'
import { invalidateLoadCompletePageConditionalRule } from '@/queries/questions/useLoadCompletePageConditionalRule'
import { useUpdateCompletePageConditionalRules } from '@/queries/questions/useUpdateCompletePageConditionalRules'
import { getSeparateCompleteConditionalRule } from '@/utils/conditionalCompletePage/getSeparateCompleteConditionalRule'

import { CustomCompletePage } from '../FormBuilderSettingsCompletePage/CustomCompletePage'
import PacketWrapper from './Components/PacketWrapper'

function PacketNotificationsSettings(props: any) {
  return <FormBuilderSettingsNotifications {...props} resourceType="packet" />
}

function PacketSettingsCustomInvite(props: any) {
  return <FormBuilderSettingsCustomInvite {...props} resourceType="packet" />
}

function noOp() {}

function createOrdering(
  packetItems: Array<{ type: 'form' | 'pdf', id: string }>
) {
  return packetItems.map((resource, index) => ({
    order: index,
    form: resource.type === 'form' ? resource.id : null,
    document: resource.type === 'pdf' ? resource.id : null,
  }))
}

function PacketRouter(): React.Node {
  const { updatePacket, isLoading: isUpdating } = useUpdatePackets()
  const { addPacket, isLoading: isCreating } = useAddPackets()
  const { addNewCompletePageConditionalRules } =
    useAddNewCompletePageConditionalRules()
  const { updateCompletePageConditionalRules } =
    useUpdateCompletePageConditionalRules()
  const { deleteCompletePageConditionalRuleById } =
    useDeleteCompletePageConditionalRuleById()
  const isLoading = isUpdating || isCreating
  const params = useParams()
  const packetId = params.packetId
  const dispatch = useDispatch()

  const { name, items, completePage, notification_users, resourceSettings } =
    useSelector((state) => state.packet)

  const history = useHistory()

  const validate = React.useCallback(() => {
    if (!name) {
      return { name: 'Packet name is required' }
    }

    if (!items?.length) {
      return { forms: 'Please select at least one form or document' }
    }
  }, [items?.length, name])

  const onUpdatePacket = React.useCallback(
    async (body) => {
      await updatePacket(packetId, body)

      if (resourceSettings != null) {
        const data = {
          id: resourceSettings.resourceId,
          use_custom_messaging: resourceSettings.useCustomInvite,
          email_subject: resourceSettings.emailSubject,
          email_content: resourceSettings.emailMessage,
          sms_content: resourceSettings.textMessage,
        }
        await createUpdateResourceSettings(packetId, data)

        const extraContexts = {
          'custom-notifications': {
            values: resourceSettings,
          },
        }
        await saveExtraContexts(extraContexts)
      }
    },
    [packetId, resourceSettings, updatePacket]
  )

  const onCreatePacket = React.useCallback(
    async (body) => {
      const response = await addPacket(body)
      if (response) {
        history.replace(`/packets/${response.id}`)
      }
    },
    [addPacket, history]
  )

  const onSave = React.useCallback(async () => {
    const error = validate()

    if (error) {
      dispatch(setErrors(error))
      return
    }

    dispatch(setErrors(null))

    const { forms, documents } = items.reduce(
      (acc, item) => {
        if (item.type === 'form') {
          acc.forms.push(item.id)
        }
        if (item.type === 'pdf') {
          acc.documents.push(item.id)
        }
        return acc
      },
      { forms: [], documents: [] }
    )

    const body = {
      name,
      forms,
      documents,
      ordering: createOrdering(items),
      notification_users: notification_users || [],
      redirect_url: completePage?.redirectUrl || null,
      custom_complete_page: completePage?.customCompletePage || null,
      has_redirect_rules: completePage?.hasRedirectRules,
    }

    const isNew = !packetId

    const hasRedirectRules = completePage?.hasRedirectRules

    if (!hasRedirectRules && completePage?.data?.length) {
      await Promise.all(
        completePage?.data
          .filter((item) => !item.is_new)
          .map((item) => deleteCompletePageConditionalRuleById(item.id || ''))
      )

      await invalidateLoadCompletePageConditionalRule({ packet: packetId })
    }

    if (completePage?.data?.length && hasRedirectRules) {
      const separateRules = getSeparateCompleteConditionalRule(
        completePage?.data
      )

      if (Object.keys(separateRules.new) && separateRules.new.length) {
        await addNewCompletePageConditionalRules(separateRules.new)
      }
      if (Object.keys(separateRules.update) && separateRules.update.length) {
        await Promise.all(
          separateRules.update.map((item) =>
            updateCompletePageConditionalRules(item)
          )
        )
      }
      await invalidateLoadCompletePageConditionalRule({ packet: packetId })
    }

    if (isNew) {
      await onCreatePacket(body)
    } else {
      await onUpdatePacket(body)
    }

    toast.success(`Packet ${packetId ? 'saved' : 'created'}`)
  }, [
    addNewCompletePageConditionalRules,
    completePage?.customCompletePage,
    completePage?.data,
    completePage?.hasRedirectRules,
    completePage?.redirectUrl,
    deleteCompletePageConditionalRuleById,
    dispatch,
    items,
    name,
    notification_users,
    onCreatePacket,
    onUpdatePacket,
    packetId,
    updateCompletePageConditionalRules,
    validate,
  ])

  const navigateToSettings = React.useCallback(() => {
    history.push(`/packets/${packetId}/settings/notifications`)
  }, [history, packetId])

  const navigateToPacket = React.useCallback(() => {
    history.push(`/packets/${packetId}`)
  }, [history, packetId])

  const settingsActive = history.location.pathname.includes('settings')

  return (
    <PacketWrapper>
      <LayoutWithSidebar
        handleBuild={navigateToPacket}
        handlePreview={noOp}
        handleSave={onSave}
        handleSettings={navigateToSettings}
        hidePreview={true}
        previewDisabled={true}
        saveLoading={isLoading}
        settingsActive={settingsActive}
        sideMenuContent={<PacketSideMenu />}
        title={'Packet Details'}
      >
        <Switch>
          <PrivateRoute
            exact
            component={PacketNotificationsSettings}
            path="/packets/:packetId/settings/notifications"
          />
          <PrivateRoute
            exact
            component={PacketSettingsCustomInvite}
            path="/packets/:packetId/settings/custom-email-and-sms-invitation"
          />
          <PrivateRoute
            exact
            component={CustomCompletePage}
            path="/packets/:packetId/settings/complete-page"
          />
          <PrivateRoute exact component={PacketDetails} path="/packets/new" />
          <PrivateRoute
            exact
            component={PacketDetails}
            path="/packets/:packetId"
          />
        </Switch>
      </LayoutWithSidebar>
    </PacketWrapper>
  )
}

export default PacketRouter
